import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { EObservableElementProp, IObservableElementProps, OBSERVABLE_ELEMENT_PROPS_SET } from '../../directives';
import { AnimationFrameService } from '../../services';
import { distinctUntilChanged, filter, map, tap } from 'rxjs/operators';
import { ComponentPrototype, isTrue } from '../../shared';

type ElemProps = IObservableElementProps;

const ROOT = 'sticky-panel';
const GHOST = 'sticky-panel__ghost-elem';
const CONTENT = `${ROOT}__content`;
const PLACEHOLDER = `${ROOT}__placeholder`;

const DEFAULT_OFFSET = 25;
const ADDITIONAL_OFFSET = 42; // GLSS-7307 ToDo: Waiting for release version

@Component({
  selector: 'app-sticky-panel',
  template: `
    <div class="sticky-panel" [class.isStacked]="isStacked$ | async" [class.mainLayer]="mainLayer">
      <div class="sticky-panel__ghost-elem"></div>
      <div [appObservableElement]="OBSERVING_PROPS" [observable$]="placeholder$">
        <div class="sticky-panel__placeholder"></div>
      </div>
      <div
        class="sticky-panel__content"
        [style.z-index]="z"
        [appObservableElement]="OBSERVING_PROPS"
        [observable$]="content$"
      >
        <ng-content></ng-content>
      </div>
    </div>
  `,
  styleUrls: ['./sticky-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StickyPanelComponent extends ComponentPrototype implements OnInit {
  @Input() public threshold: number = 0;

  @Input() public z: number = 1000;

  @Input() public mainLayer: boolean = false;

  @Input() public set disabled(disabled: boolean) {
    const { disabled$ } = this;
    disabled$.next(disabled);
  }

  @Output() public offsetChange: EventEmitter<number> = new EventEmitter<number>();

  @Output() public viewChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  public readonly OBSERVING_PROPS: EObservableElementProp[] = OBSERVABLE_ELEMENT_PROPS_SET.SIZE_AND_POSITION;

  public readonly isStacked$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public readonly content$: BehaviorSubject<ElemProps> = new BehaviorSubject<ElemProps>(null);

  public readonly placeholder$: BehaviorSubject<ElemProps> = new BehaviorSubject<ElemProps>(null);

  public readonly offset$: BehaviorSubject<number> = new BehaviorSubject<number>(0);

  public readonly disabled$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(private ref: ElementRef, private service: AnimationFrameService) {
    super();
  }

  ngOnInit(): void {
    const {
      isStacked$,
      content$,
      placeholder$,
      offset$,
      disabled$,
      offsetChange,
      viewChange,
      service,
      threshold,
      ref,
      mainLayer,
    } = this;
    const ghostElem = ref.nativeElement.querySelector(`.${GHOST}`);
    const contentElem = ref.nativeElement.querySelector(`.${CONTENT}`);
    const placeholderElem = ref.nativeElement.querySelector(`.${PLACEHOLDER}`);

    const refreshStackedState = ([placeholderProps, offset]): void => {
      if (disabled$.value) {
        isStacked$.next(false);
      } else {
        const isStacked = placeholderProps.top < offset + threshold;
        isStacked !== isStacked$.value && isStacked$.next(isStacked);
      }
    };

    const getStaticOffset = (): number => {
      return DEFAULT_OFFSET + (mainLayer ? 0 : ADDITIONAL_OFFSET);
    };

    const refreshStyles = ([contentProps, placeholderProps, offset, isStacked]): void => {
      const staticOffset = getStaticOffset();

      ghostElem.style.top = `${offset - staticOffset}px`;
      contentElem.style.left = `${placeholderProps.left}px`;
      contentElem.style.top = `${offset + threshold}px`;

      if (isStacked) {
        ghostElem.style.height = `${staticOffset}px`;
        placeholderElem.style.height = `${contentProps.height}px`;
        contentElem.style.width = `${placeholderProps.width}px`;
        contentElem.style.position = 'fixed';
      } else {
        ghostElem.style.height = '0px';
        placeholderElem.style.height = '0px';
        contentElem.style.width = '100%';
        contentElem.style.position = 'static';
      }
    };

    const getOffset = (): number => {
      return (document.querySelector('.search-area')?.getBoundingClientRect()?.bottom || 0) + getStaticOffset();
    };

    this.registerSubscriptions(
      service.on$
        .pipe(
          map(getOffset),
          distinctUntilChanged(),
          tap(offset => offset$.next(offset))
        )
        .subscribe(),

      combineLatest([placeholder$, offset$])
        .pipe(
          filter(values => values.every(isTrue)),
          tap(refreshStackedState)
        )
        .subscribe(),

      combineLatest([content$, placeholder$, offset$, isStacked$])
        .pipe(
          filter(([a, b]) => [a, b].every(isTrue)),
          tap(refreshStyles)
        )
        .subscribe(),

      isStacked$
        .pipe(
          distinctUntilChanged(),
          tap(isStacked => viewChange.emit(isStacked))
        )
        .subscribe(),

      offset$.pipe(tap(offset => offsetChange.emit(offset))).subscribe()
    );
  }
}
